/**
 * Revision Analytics Hook
 *
 * Handles tracking of rewrite events (accept/ignore/modify decisions)
 * and PostHog analytics for the revision workflow.
 *
 * Extracted from useRevisionTracking.ts for single-responsibility clarity.
 */

import { useCallback, useRef } from 'react';
import {
  createRewriteEvent,
  type CreateRewriteEventInput,
} from '@dramadetector/api-client';
import { trackEvent, type UserTier } from '@/lib/analytics/events';
import { mapStatusToDecision, mapSeverity } from './useScoreCalculation';
import { normalizeError, type TrackingErrorHandler } from './revision-utils';
import type { InlineIssue, IssueStatus } from '../types';

interface UseRevisionAnalyticsOptions {
  enabled: boolean;
  tier: UserTier;
  totalIssues: number;
  ensureRevisionSession: () => Promise<string | null>;
  issueDisplayTimesRef: React.RefObject<Map<number, number>>;
  decisionLatenciesRef: React.RefObject<number[]>;
  revisionIdRef: React.RefObject<string | null>;
  onTrackingError?: TrackingErrorHandler;
}

interface UseRevisionAnalyticsReturn {
  /** Track a rewrite event (accept/ignore decision) */
  trackRewriteEvent: (issue: InlineIssue, status: IssueStatus, issueIndex: number) => Promise<void>;
  /** Track a modified rewrite event (user edited the suggestion) */
  trackModifiedEvent: (issue: InlineIssue, modifiedText: string) => Promise<void>;
  /** Whether tracking is in progress (non-reactive ref snapshot) */
  isTrackingRef: React.RefObject<boolean>;
}

/**
 * Hook for tracking revision analytics events.
 *
 * Handles both standard accept/ignore decisions and user-modified rewrites.
 * All tracking is non-blocking (fire-and-forget with error reporting).
 */
export function useRevisionAnalytics({
  enabled,
  tier,
  totalIssues,
  ensureRevisionSession,
  issueDisplayTimesRef,
  decisionLatenciesRef,
  revisionIdRef,
  onTrackingError,
}: UseRevisionAnalyticsOptions): UseRevisionAnalyticsReturn {
  const isTrackingRef = useRef(false);

  /**
   * Track a rewrite event (accept/ignore decision)
   */
  const trackRewriteEvent = useCallback(
    async (issue: InlineIssue, status: IssueStatus, issueIndex: number) => {
      // Skip pending status (no decision made)
      if (status === 'pending') return;
      if (!enabled) return;

      isTrackingRef.current = true;

      try {
        const sessionId = await ensureRevisionSession();
        if (!sessionId) {
          isTrackingRef.current = false;
          return;
        }

        // Calculate decision latency
        const displayTime = issueDisplayTimesRef.current.get(issue.id);
        const latencyMs = displayTime ? Date.now() - displayTime : undefined;

        if (latencyMs !== undefined) {
          decisionLatenciesRef.current.push(latencyMs);
        }

        const decision = mapStatusToDecision(status);
        if (decision === null) {
          isTrackingRef.current = false;
          return;
        }

        const input: CreateRewriteEventInput = {
          revisionId: sessionId,
          issueId: String(issue.id),
          issueType: issue.type,
          severity: mapSeverity(issue.severity),
          snippet: issue.snippet,
          replacement: issue.replacement,
          startIndex: issue.startIndex,
          endIndex: issue.endIndex,
          decision,
          decisionLatencyMs: latencyMs,
        };

        const { error } = await createRewriteEvent(input);

        if (error) {
          console.error('[RevisionTracking] Failed to track event:', error);
          onTrackingError?.(new Error(error.message), 'createRewriteEvent');
          trackEvent('Revision_Tracking_Error', {
            context: 'createRewriteEvent',
            error_message: error.message,
            revision_id: sessionId,
            tier,
          });
        } else {
          trackEvent('Revision_Rewrite_Tracked', {
            revision_id: sessionId,
            issue_type: issue.type,
            severity: mapSeverity(issue.severity),
            decision,
            decision_latency_ms: latencyMs,
            issue_index: issueIndex,
            issues_total: totalIssues,
            tier,
          });
        }
      } catch (err) {
        console.error('[RevisionTracking] Event tracking error:', err);
        const { error: normalizedErr, message: errorMessage } = normalizeError(err);
        onTrackingError?.(normalizedErr, 'createRewriteEvent');
        trackEvent('Revision_Tracking_Error', {
          context: 'createRewriteEvent',
          error_message: errorMessage,
          revision_id: revisionIdRef.current ?? undefined,
          tier,
        });
      } finally {
        isTrackingRef.current = false;
      }
    },
    [enabled, ensureRevisionSession, totalIssues, tier, onTrackingError, issueDisplayTimesRef, decisionLatenciesRef, revisionIdRef]
  );

  /**
   * Track a modified rewrite event (DD-550: user edited the suggestion)
   */
  const trackModifiedEvent = useCallback(
    async (issue: InlineIssue, modifiedText: string) => {
      if (!enabled) return;

      try {
        const sessionId = await ensureRevisionSession();
        if (!sessionId) return;

        const displayTime = issueDisplayTimesRef.current.get(issue.id);
        const latencyMs = displayTime ? Date.now() - displayTime : undefined;
        if (latencyMs !== undefined) decisionLatenciesRef.current.push(latencyMs);

        const input: CreateRewriteEventInput = {
          revisionId: sessionId,
          issueId: String(issue.id),
          issueType: issue.type,
          severity: mapSeverity(issue.severity),
          snippet: issue.snippet,
          replacement: issue.replacement,
          startIndex: issue.startIndex,
          endIndex: issue.endIndex,
          decision: 'modified',
          userModifiedText: modifiedText,
          decisionLatencyMs: latencyMs,
        };

        const { error } = await createRewriteEvent(input);

        if (error) {
          console.error('[RevisionTracking] Failed to track modified event:', error);
          onTrackingError?.(new Error(error.message), 'createRewriteEvent');
        } else {
          trackEvent('rewrite_modified', {
            suggestion_id: String(issue.id),
            original_text: issue.snippet,
            suggested_text: issue.replacement || '',
            modified_text: modifiedText,
            issue_type: issue.type,
            severity: mapSeverity(issue.severity),
            revision_id: sessionId,
            tier,
          });
        }
      } catch (err) {
        console.error('[RevisionTracking] Modified event tracking error:', err);
        const { error: normalizedErr } = normalizeError(err);
        onTrackingError?.(normalizedErr, 'createRewriteEvent');
      }
    },
    [enabled, ensureRevisionSession, tier, onTrackingError, issueDisplayTimesRef, decisionLatenciesRef]
  );

  return { trackRewriteEvent, trackModifiedEvent, isTrackingRef };
}
